import { createAnalyticsClient, type AnalyticsClientEnv } from "./createAnalyticsClient";
import type { AnalyticsClient } from "./AnalyticsClient";

// ── the thin local interface every page uses ────────────────────────────────
// Ported from enderlane's src/analytics/analytics.ts. `initAnalytics` is
// called once — from AppWithConfig.tsx, and ONLY once both /config.json has
// resolved AND the visitor has accepted the cookie-consent banner (see
// src/_providers/CookieConsentProvider.tsx) — with whatever /config.json
// resolved (see useSiteConfig — posthogKey/posthogHost). `track` is the ONLY
// thing a capture site ever calls; it must never be able to break the
// product — a throwing vendor call or a still-uninitialised client must be
// swallowed, not propagated.
let client: AnalyticsClient | null = null;
let initialized = false;

export function initAnalytics(env: AnalyticsClientEnv): void {
  // Idempotent — "analytics initialises once". A second call (e.g. a stray
  // re-render) must never construct a second client.
  if (initialized) return;
  initialized = true;
  client = createAnalyticsClient(env);
}

export function track(event: string, props?: Record<string, unknown>): void {
  // Analytics must never be able to break the product. With no client
  // constructed (no key configured, consent not yet granted, or not yet
  // initialised) this is a silent no-op; with a client, a throwing vendor
  // call is swallowed rather than propagated.
  if (!client) return;
  try {
    client.capture(event, props);
  } catch {
    // Swallow — the user must see nothing.
  }
}

/** Test-only escape hatch so analytics.test.ts can reset the module singleton between cases. */
export function __resetAnalyticsForTests(): void {
  client = null;
  initialized = false;
}
